import JSONbig from 'json-bigint'
import { API_BASE } from './config.js'

const jsonParser = JSONbig({ storeAsString: true })

function getToken() {
  return localStorage.getItem('token') || ''
}

function request(method, path, body) {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + getToken()
  }
  const options = {
    method,
    headers,
    credentials: 'include'
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  return fetch(`${API_BASE}${path}`, options)
    .then(res => res.text())
    .then(text => jsonParser.parse(text))
}

/**
 * 查询我的随想列表（按创建时间倒序，新记的排最前）
 * @returns {Promise<{code:number,message:string,data:Array,success:boolean,timestamp:string}>}
 */
export function listThoughts() {
  return request('GET', '/api/v1/thoughts')
}

/**
 * 记下一条随想
 * @param {Object} params
 * @param {string} params.content 随想内容（必填，1-255 字）
 * @param {string} [params.category] 分类：WORK / LIFE / STUDY / OTHER，默认 OTHER
 * @param {string} [params.priority] 优先级：HIGH / MEDIUM / LOW，默认 MEDIUM
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function createThought(params) {
  return request('POST', '/api/v1/thoughts', params)
}

/**
 * 修改随想（内容 / 分类 / 优先级）
 * @param {string|number} id 随想 ID（雪花 ID，字符串）
 * @param {Object} params
 * @param {string} [params.content] 随想内容
 * @param {string} [params.category] 分类：WORK / LIFE / STUDY / OTHER
 * @param {string} [params.priority] 优先级：HIGH / MEDIUM / LOW
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function updateThought(id, params) {
  return request('PUT', `/api/v1/thoughts/${id}`, params)
}

/**
 * 安排随想时间；startTime 传 null 表示取消安排
 * @param {string|number} id 随想 ID
 * @param {Object} params
 * @param {string|null} params.startTime 开始时间（ISO 8601）
 * @param {string|null} [params.endTime] 结束时间（ISO 8601）
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function scheduleThought(id, params) {
  return request('PUT', `/api/v1/thoughts/${id}/schedule`, params)
}

/**
 * 完成 / 撤销完成随想
 * @param {string|number} id 随想 ID
 * @param {boolean} completed true 完成，false 撤销完成
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function completeThought(id, completed) {
  return request('PUT', `/api/v1/thoughts/${id}/complete`, { completed })
}

/**
 * 把随想转为时间块（出箱）
 *
 * 后端在一个事务里完成「建时间块 + 随想出箱」，不会出现建块成功但收集箱还留一条的情况。
 * 随想不再软删，而是置为 CONVERTED 并记录 convertedBlockId，列表默认不再返回它。
 *
 * @param {string|number} id 随想 ID
 * @param {Object} params
 * @param {string|number} params.workspaceId 目标工作区 ID（必填）
 * @param {string|number} params.projectId 目标工作表 ID（必填）
 * @param {string|number} [params.tagId] 目标分类（标签）ID，可为空
 * @param {string} params.startTime 计划开始时间（ISO 8601）
 * @param {string} params.endTime 计划结束时间（ISO 8601）
 * @param {string} [params.color] 时间块颜色 #RRGGBB，取自标签色，脏数据不要传
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function convertThought(id, params) {
  return request('POST', `/api/v1/thoughts/${id}/convert`, params)
}

/**
 * 批量删除随想（软删除），用于「清空已完成」
 *
 * 后端逐条校验归属，任一 ID 越权就整批回滚，不会出现删了一半的情况。
 *
 * @param {Array<string|number>} ids 随想 ID 列表，最多 200 条
 * @returns {Promise<{code:number,message:string,data:{count:number},success:boolean,timestamp:string}>}
 *   data.count = 实际删除条数（请求里可能混入已被删除或不存在的 ID）
 */
export function batchDeleteThoughts(ids) {
  return request('POST', '/api/v1/thoughts/batch-delete', { ids })
}

/**
 * 删除随想（软删除）
 * @param {string|number} id 随想 ID
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function deleteThought(id) {
  return request('DELETE', `/api/v1/thoughts/${id}`)
}
